import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brief, Inspector, TONE_WORD } from '../command/CommandView';
import { stationKey, stationByKey } from './stations';
import { useRoomAudio } from './roomAudio';
import { REPLAY_SPEEDS } from '../command/replayClock';
import './room.css';

// The chrome around a room, kept thin so the world is the page: a top bar
// (breadcrumb · the station chips · mode · fullscreen · help · exit), the
// brief band along the bottom (hover, or the room's default card), the
// pinned inspector down the right edge, and the `?` sheet with the key
// map. Cards are the command view's own components, so a room says the
// same words as the stage it came from.

const KEYS = [
  ['drag', 'orbit'], ['right-drag · ⇧drag', 'pan'], ['wheel', 'zoom'], ['click', 'fly to a part and pin its card'],
  ['double-click', 'open the details'], ['1 – 9', 'fly to a station'], ['C', 'cruise on / off'],
  ['W A S D · arrows', 'move (cruise)'], ['Q / E', 'sink / rise (cruise)'], ['⇧', 'faster (cruise)'],
  ['R', 'home view'], ['F', 'fullscreen'], ['Space', 'replay the window · pause'], ['M', 'sound on / off'], ['?', 'this sheet'], ['Esc', 'peel one layer, then leave'],
];

const typing = (e) => { const t = e.target; return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable); };

export function toggleFullscreen(el) {
  const root = el || document.documentElement;
  if (document.fullscreenElement) document.exitFullscreen?.();
  else root.requestFullscreen?.();
}

// The key map. `onEscape` returns true when it consumed the key (peeled a
// layer); otherwise the room leaves via `onExit`.
export function useRoomKeys({ stations = [], onStation, ctl, onEscape, onExit, rootRef, onHelp, enabled = true, replay = null, audio = null }) {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (typing(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key;
      if (k === 'Escape') {
        if (onHelp?.(false)) return;
        if (replay?.active) { replay.stop(); return; }
        if (ctl?.cruise) { ctl.setCruise(false); return; }
        if (onEscape?.()) return;
        if (document.fullscreenElement) { document.exitFullscreen?.(); return; }
        onExit?.();
        return;
      }
      if (k === '?') { onHelp?.('toggle'); return; }
      if (k === ' ' && replay) { e.preventDefault(); replay.toggle(); return; }
      const lower = k.toLowerCase();
      if (lower === 'c') { ctl?.setCruise(!ctl.cruise); return; }
      if (lower === 'f') { toggleFullscreen(rootRef?.current); return; }
      if (lower === 'r') { ctl?.home(); return; }
      if (lower === 'm' && audio) { audio.toggle(); return; }
      const s = stationByKey(stations, k);
      if (s) { e.preventDefault(); onStation?.(s); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stations, onStation, ctl, onEscape, onExit, rootRef, onHelp, enabled, replay, audio]);
}

const fmtClock = (t) => (t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
// The replay transport: play / pause, the time under the cursor, a scrub
// bar, the speed. Lives in the HUD; the clock itself is `replay`.
function ReplayBar({ replay }) {
  // The scene advances the clock every frame; the bar follows at 3 Hz while playing.
  const [, tick] = useState(0);
  const playing = !!replay?.playing;
  useEffect(() => { if (!playing) return undefined; const t = setInterval(() => tick((x) => x + 1), 330); return () => clearInterval(t); }, [playing]);
  if (!replay) return null;
  const u = replay.u();
  return (
    <div className={`room-replay ${replay.active ? 'is-active' : ''}`} role="group" aria-label="Replay">
      <button type="button" className="room-chip" onClick={() => replay.toggle()} title={replay.playing ? 'Pause (Space)' : 'Replay the window (Space)'}>
        <kbd>␣</kbd>{replay.active ? (replay.playing ? 'pause' : 'play') : 'replay'}
      </button>
      {replay.active && (
        <>
          <span className="room-replay-time">{fmtClock(replay.t)}</span>
          <input type="range" min="0" max="1000" value={Math.round(u * 1000)} onChange={(e) => replay.seek(Number(e.target.value) / 1000)} aria-label="Scrub" />
          <span className="room-replay-speeds">
            {REPLAY_SPEEDS.map((s) => <button key={s} type="button" className={`room-chip ${replay.speed === s ? 'is-active' : ''}`} onClick={() => replay.setSpeed(s)}>{s >= 60 ? `${s / 60}m/s` : `${s}×`}</button>)}
          </span>
          <button type="button" className="room-chip" onClick={() => replay.stop()} title="Back to live (Esc)">live</button>
        </>
      )}
    </div>
  );
}

export default function RoomHud({
  crumbs = [], stations = [], activeStation = null, onStation, ctl, onExit, exitLabel = 'Exit', rootRef,
  chips = null, brief = null, briefHint = null, briefDefault = true, pinned = null, onOpen, onClear,
  simulated = false, scenario = null, children, title = null, replay = null, viewers = null,
}) {
  const [help, setHelp] = useState(false);
  const audio = useRoomAudio();
  const onHelp = useCallback((v) => {
    if (v === 'toggle') { setHelp((h) => !h); return true; }
    if (v === false) { let was = false; setHelp((h) => { was = h; return false; }); return was; }
    return false;
  }, []);
  useRoomKeys({ stations, onStation, ctl, onEscape: () => { if (pinned) { onClear?.(); return true; } return false; }, onExit, rootRef, onHelp, replay, audio });
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const on = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  return (
    <>
      <div className="room-top" role="toolbar" aria-label="Room controls">
        <nav className="room-crumbs" aria-label="Breadcrumb">
          {crumbs.map((c, i) => (
            <React.Fragment key={`${c.label}-${i}`}>
              {i > 0 && <span className="room-crumb-sep">›</span>}
              {c.to ? <Link to={c.to} className="room-crumb">{c.label}</Link>
                : c.onClick ? <button type="button" className="room-crumb" onClick={c.onClick}>{c.label}</button>
                  : <span className="room-crumb is-current">{c.label}</span>}
            </React.Fragment>
          ))}
          {title && <span className="room-title">{title}</span>}
        </nav>
        <div className="room-stations" role="group" aria-label="Stations">
          {stations.slice(0, 9).map((s, i) => (
            <button key={s.id} type="button" className={`room-chip ${activeStation === s.id ? 'is-active' : ''}`} onClick={() => onStation?.(s)} title={`${s.label} · ${stationKey(stations, i)}`}>
              <kbd>{stationKey(stations, i)}</kbd>{s.label}
            </button>
          ))}
        </div>
        <div className="room-tools">
          {children}
          {ctl && (
            <button type="button" className={`room-chip ${ctl.cruise ? 'is-active' : ''}`} onClick={() => ctl.setCruise(!ctl.cruise)} aria-pressed={ctl.cruise} title="Cruise: W A S D move · Q E sink rise · ⇧ faster (C)">
              <kbd>C</kbd>{ctl.cruise ? 'cruising' : 'cruise'}
            </button>
          )}
          {ctl && <button type="button" className="room-chip" onClick={() => ctl.home()} title="Home view (R)"><kbd>R</kbd>home</button>}
          <button type="button" className={`room-chip ${audio.muted ? '' : 'is-active'}`} onClick={audio.toggle} aria-pressed={!audio.muted} title="Sound: a tick per request, a tone per probe (M)"><kbd>M</kbd>{audio.muted ? 'sound off' : 'sound on'}</button>
          <button type="button" className={`room-chip ${fs ? 'is-active' : ''}`} onClick={() => toggleFullscreen(rootRef?.current)} title="Fullscreen (F)"><kbd>F</kbd>{fs ? 'windowed' : 'fullscreen'}</button>
          <button type="button" className={`room-chip ${help ? 'is-active' : ''}`} onClick={() => setHelp((h) => !h)} aria-expanded={help} title="Keys (?)"><kbd>?</kbd>keys</button>
          {onExit && <button type="button" className="room-chip is-exit" onClick={onExit} title="Leave the room (Esc)">{exitLabel}</button>}
        </div>
      </div>
      {(chips || replay || viewers?.length > 0) && (
        <div className="room-chips">
          {chips}
          {viewers?.length > 0 && <span className="room-viewers" title={viewers.map((v) => v.name).join(', ')}>{viewers.length === 1 ? `${viewers[0].name || 'someone'} is here too` : `${viewers.length} others here`}</span>}
          <ReplayBar replay={replay} />
        </div>
      )}
      {replay?.active && <div className="cmd-watermark room-watermark" aria-label="Replaying">REPLAY · {fmtClock(replay.t)}</div>}
      {simulated && !replay?.active && <div className="cmd-watermark room-watermark" aria-label="Simulated data">SIMULATED{scenario && scenario !== 'calm' ? ` · ${scenario}` : ''}</div>}
      {brief && (
        <div className="room-brief">
          <Brief card={brief} isDefault={briefDefault} hint={briefHint} />
        </div>
      )}
      {pinned && (
        <aside className="room-inspector">
          <Inspector card={pinned} onOpen={onOpen} onClear={onClear} hint="live · Esc clears" />
        </aside>
      )}
      {help && (
        <div className="room-help" role="dialog" aria-label="Keys">
          <div className="room-help-head"><strong>Moving around</strong><button type="button" className="cmd-inspector-x" onClick={() => setHelp(false)} aria-label="Close">×</button></div>
          <dl>
            {KEYS.map(([k, v]) => <React.Fragment key={k}><dt><kbd>{k}</kbd></dt><dd>{v}</dd></React.Fragment>)}
          </dl>
          <p className="room-help-note">Position is where a part sits in the system. Anything that moves is a measurement, or you.</p>
        </div>
      )}
    </>
  );
}

export { TONE_WORD };
